<template>
  <e-chart
    v-if="hasData"
    :options="chartOptions"
    autoresize
    class="apache-echarts-hotspot-bubble-graph"
    @click="handleChartClick"
  ></e-chart>
  <div v-else-if="loaded">
    {{ $t("No Data Available") }}
  </div>
  <SpinnerCmpt v-else></SpinnerCmpt>
</template>

<script>
import moment from 'moment';

import SpinnerCmpt from '@/xvisor/components/SpinnerCmpt.vue';
import colorPalette from '@/xvisor/constants/colorPalette';
import colorPaletteShade from '@/xvisor/constants/colorPaletteShade';
import granularity from '@/xvisor/utilities/granularity';
import numberUtils from '@/xvisor/utilities/numberUtils';
import themeStyle from '@/xvisor/utilities/themeStyle';
import timeFormat from '@/xvisor/utilities/timeFormat';

const SYMBOL_SIZE_MAX = 30;
const SYMBOL_SIZE_MIN = 1;

export default {
  props: {
    url: {
      type: String,
      required: true,
    },
    timeRange: {
      type: Object,
      required: true,
    },
    hotspotKey: {
      type: String,
      required: true,
    },
  },
  components: {
    SpinnerCmpt,
  },
  data() {
    return {
      countData: [],
      loaded: false,
    };
  },
  mounted() {
    this.httpGet();
  },
  watch: {
    timeRange() {
      this.httpGet();
    },
    url() {
      this.httpGet();
    },
  },
  methods: {
    httpGet() {
      this.loaded = false;
      this.$http
        .get(this.url, {
          params: {
            start: this.timeRange.start.toISOString(),
            end: this.timeRange.end.toISOString(),
            stepMinutes: this.stepMinutes,
          },
        })
        .then((response) => { this.countData = response.data; })
        .finally(() => { this.loaded = true; });
    },
    scaleValue(value) {
      const scaledValue = numberUtils.mapNumberFromRangeToRange(
        value,
        this.valueMin,
        this.valueMax,
        SYMBOL_SIZE_MIN,
        SYMBOL_SIZE_MAX,
      );
      return scaledValue;
    },
    formatAxisLabel(value) {
      if (this.timeSeriesFormat === 'MMM D, hh:mm A') {
        return [
          moment(value).format('MMM D'),
          moment(value).format('hh:mm A'),
        ].join('\n');
      }
      if (this.timeSeriesFormat === 'MMM D, hh A') {
        return [
          moment(value).format('MMM D'),
          moment(value).format('hh A'),
        ].join('\n');
      }
      return moment(value).format(this.timeSeriesFormat);
    },
    roundDownToStep(time) {
      const stepMillis = this.stepMinutes * 60 * 1000;
      return Math.floor(time.getTime() / stepMillis) * stepMillis;
    },
    roundUpToStep(time) {
      const stepMillis = this.stepMinutes * 60 * 1000;
      return Math.ceil(time.getTime() / stepMillis) * stepMillis;
    },
    handleChartClick(params) {
      if (params.componentType !== 'series') return;
      if (!params.data || params.data[1] === 0) return;
      const pointStart = new Date(params.data[0]);
      const pointEnd = new Date(Math.min(
        pointStart.getTime() + (this.stepMinutes * 60 * 1000),
        this.timeRange.end.getTime(),
      ));
      this.$emit('hotspot-click', {
        hotspotKey: this.hotspotKey,
        point: params.data,
        timeRange: {
          start: pointStart,
          end: pointEnd,
        },
      });
    },
  },
  computed: {
    hasData() {
      return this.loaded && this.countData && this.countData.length > 0;
    },
    stepMinutes() {
      return granularity.granularity6(this.timeRange);
    },
    allTimes() {
      return this.countData.map((point) => point.time);
    },
    allValues() {
      return this.countData.map((point) => point.value);
    },
    timeSet() {
      return new Set(this.allTimes);
    },
    timeRangeMax() {
      return Math.max(...this.allTimes.map((element) => new Date(element).getTime()));
    },
    timeRangeMin() {
      return Math.min(...this.allTimes.map((element) => new Date(element).getTime()));
    },
    chartTimeRangeMax() {
      return this.roundUpToStep(new Date(this.timeRangeMax));
    },
    chartTimeRangeMin() {
      return this.roundDownToStep(new Date(this.timeRangeMin));
    },
    valueMax() {
      return Math.max(...this.allValues);
    },
    valueMin() {
      return Math.min(...this.allValues);
    },
    timeSeriesFormat() {
      return timeFormat.momentFormat(this.timeSet, new Date());
    },
    toggleColor() {
      return themeStyle.styleToggle(colorPalette.black, colorPalette.white);
    },
    chartOptions() {
      return {
        title: {
          text: 'Affected Apps',
          textStyle: {
            color: this.toggleColor,
            fontSize: 10,
          },
        },
        color: [colorPaletteShade.orange4, colorPaletteShade.red4],
        tooltip: {
          trigger: 'axis',
          position: 'top',
          renderMode: 'html',
          appendToBody: true,
          extraCssText: 'z-index: 9999;',
          formatter: (info) => `
            <div class="apache-echarts-tooltip">
              <div class="apache-echarts-tooltip-timestamp">
                ${moment(info[0].value[0]).format('MMM D h:mm A')}
              </div>
              <div>
                ${info[0].marker} App Count: ${info[0].value[1]}
              </div>
            </div>
          `,
        },
        singleAxis: [
          {
            min: this.chartTimeRangeMin,
            max: this.chartTimeRangeMax,
            top: '34%',
            left: 52,
            right: 36,
            height: '15%',
            type: 'time',
            data: this.allTimes,
            axisLabel: {
              interval: 2,
              margin: 12,
              color: this.toggleColor,
              showMinLabel: true,
              showMaxLabel: true,
              formatter: (value) => this.formatAxisLabel(value),
            },
            axisLine: {
              lineStyle: {
                color: this.toggleColor,
              },
            },
          },
        ],
        series: [
          {
            singleAxisIndex: 0,
            type: 'scatter',
            coordinateSystem: 'singleAxis',
            data: this.countData.map((point) => ([point.time, point.value])),
            symbolSize: (value) => this.scaleValue(value[1]),
          },
        ],
      };
    },
  },
};
</script>

<style scoped>
.apache-echarts-hotspot-bubble-graph {
  width: 100% !important;
  height: 50px !important;
  cursor: pointer;
}
</style>
